import type { LeadFlowAnswers, LeadFlowOfficeSubmission } from "../types";

import {
  CARE_TYPE_VALUES,
  HONEYPOT_FIELD_NAME,
  TIMING_TOKEN_FIELD_NAME,
} from "@homeinstead/forms/lead-form";
import { LEAD_SOURCE } from "../constants/lead-source";

const FORM_VARIANT = "lfb";

type CreateFormDataInput = {
  answers: LeadFlowAnswers;
  formElement: HTMLFormElement;
  officeSubmission: LeadFlowOfficeSubmission;
  versionId: string | null;
};

function setOptionalValue(
  formData: FormData,
  name: string,
  value: number | string | undefined,
) {
  if (value !== undefined && value !== "") {
    formData.set(name, String(value));
  }
}

/**
 * Builds the legacy care seeker submission from the answer store, so the
 * server action and its validation stay shared with legacy forms. The
 * honeypot and timing token come from their rendered inputs. An office-local
 * flow submits the page office in place of a picked one.
 */
function createFormData({
  answers,
  formElement,
  officeSubmission,
  versionId,
}: CreateFormDataInput): FormData {
  const formData = new FormData();

  for (const name of [HONEYPOT_FIELD_NAME, TIMING_TOKEN_FIELD_NAME]) {
    const field = formElement.elements.namedItem(name);
    formData.set(name, field instanceof HTMLInputElement ? field.value : "");
  }

  formData.set("formVariant", FORM_VARIANT);
  formData.set("leadSource", LEAD_SOURCE);
  setOptionalValue(formData, "formVersionId", versionId ?? undefined);

  setOptionalValue(formData, "careFor", answers.careFor);
  for (const careType of CARE_TYPE_VALUES) {
    if (answers.careType?.[careType]) {
      formData.set(`careType.${careType}`, "on");
    }
  }
  setOptionalValue(formData, "urgency", answers.urgency);

  setOptionalValue(formData, "firstName", answers.firstName);
  setOptionalValue(formData, "lastName", answers.lastName);
  setOptionalValue(formData, "email", answers.email);
  setOptionalValue(formData, "phone", answers.phone);
  // The action reads SMS consent from `consentTerms`.
  if (answers.OptInSms) {
    formData.set("consentTerms", "on");
  }

  const postalCode = officeSubmission?.postalCode ?? answers.postalCode;
  setOptionalValue(formData, "postalCode", postalCode);
  setOptionalValue(
    formData,
    "location",
    officeSubmission ? officeSubmission.postalCode : answers.location,
  );
  setOptionalValue(
    formData,
    "organizationNumber",
    officeSubmission?.organizationNumber ?? answers.organizationNumber,
  );

  return formData;
}

export { createFormData };
